import { createRef } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { SearchInput } from '@nerdwallet/search-components';
import config from '@nerdwallet/app-config';
import { getLogger } from '@nerdwallet/logging';
import { useStore } from '@nanostores/preact';

import { AnalyticsProvider } from '@nerdwallet/seo-analytics';
import ApolloWrapper from '../auth/global/ApolloWrapper';
import { trackElementInteraction } from '../lib/tracking';
import MagnifyingGlass from '../auth/svg/magnifying-glass';
import CloseButton from '../auth/buttons/CloseButton';
import ErrorBoundary from '../lib/ErrorBoundary';
import {
  $openedMenu,
  $onCloseMenuCallback,
  $rightButtonsReady,
  $searchLoaded,
} from '../store/client';

import styles from './Search.module.less';

interface Props {
  includeSearch?: boolean;
}

const Search: React.FC<Props> = ({ includeSearch = true }) => {
  const logger = getLogger('right-buttons');
  logger.info('Search island is loading...');

  const [checkboxInputChecked, setCheckboxInputChecked] = useState(false);
  const checkboxInputRef = createRef();
  const rightButtonsReady = useStore($rightButtonsReady);

  // Sets $searchLoaded when this component is ready to render client-side
  useEffect(() => {
    $searchLoaded.set(true);
  }, []);

  const trackSearchOpen = (type: string, event) => {
    if (!event.target.checked) {
      return;
    }
    trackElementInteraction.bedrock({
      entityName: 'global_header_search_button',
      action: 'open',
      interactionType: type.toLowerCase(),
    });
  };

  const onCheckboxClick = (event) => {
    try {
      trackSearchOpen('press', event);
      checkboxInputRef.current.checked = true;
      setCheckboxInputChecked(checkboxInputRef.current.checked);
      $openedMenu.set('search');
    } catch (err) {
      logger.debug('Search click is not working', err);
    }
  };

  const closeButtonClick = () => {
    const checkbox = checkboxInputRef.current;
    if (!checkbox) return;
    checkbox.checked = false;
    setCheckboxInputChecked(false);
    $openedMenu.set('');
  };

  useEffect(() => {
    $onCloseMenuCallback.set(closeButtonClick);

    return () => {
      $onCloseMenuCallback.set(null);
    };
  }, [closeButtonClick]);

  if (!includeSearch || !rightButtonsReady) {
    return null;
  }

  return (
    <ErrorBoundary>
      <ApolloWrapper>
        <AnalyticsProvider>
          <div className={styles.searchWrapper}>
            <input
              type="checkbox"
              className={styles.searchButtonCheckbox}
              id="searchButtonCheckbox"
              name="searchButtonCheckbox"
              onClick={(event) => onCheckboxClick(event)}
              ref={checkboxInputRef}
            />
            <label
              for="searchButtonCheckbox"
              className={styles.searchButton}
              aria-label="Search"
            >
              <MagnifyingGlass width={20} height={20} />
            </label>
            {checkboxInputChecked ? (
              <div className={styles.searchDropdown}>
                <div className={styles.searchFormContainer}>
                  <SearchInput
                    className={styles.searchInput}
                    searchBaseUri={
                      config.SITE_BASE_URL || 'https://www.nerdwallet.com'
                    }
                  />
                </div>
                <CloseButton onClick={closeButtonClick} />
              </div>
            ) : (
              checkboxInputRef?.current
            )}
          </div>
        </AnalyticsProvider>
      </ApolloWrapper>
    </ErrorBoundary>
  );
};

export default Search;
